import { memo, useEffect, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import {
  useHasAclAccess,
  useAnalyticsOrgLocations,
  DASHBOARD_CHART_SIZE,
  DemoChip,
  OverviewInsights,
  appendTime,
  dashboardChartStyle,
} from '@frontend/analytics-shared';
import { Chart, XAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { messagingCategoriesLabels } from '../components/charts/messages-categories-chart';
import { MessagingAnalyticsFeatureFlags } from '../feature-flags';
import { useFetchAggregatedSMSReport } from '../hooks/use-fetch-aggregated-sms-report';
import { useIsMessagingAnalyticsDemoAccount } from '../hooks/use-is-messaging-analytics-demo-account';

type Props = {
  filters?: AnalyticsCommonTypes.AnalyticsSummaryFilters;
  onLoadData?: (totalMessageCount: number) => void;
  onLoadingStateChange?: (isLoading?: boolean) => void;
};

type SummedCategories = {
  [category: string]: number;
};

const categoryColors = [
  theme.colors.secondary.seaweed40,
  theme.colors.tangerine40,
  theme.colors.rose40,
  theme.colors.secondary.eggplant40,
  theme.colors.critical40,
];

export const MessagingCategories = memo(({ filters, onLoadData, onLoadingStateChange }: Props) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const messagingAnalyticsEnabled = getFeatureFlagValue(MessagingAnalyticsFeatureFlags.featureEnabled);
  const isDemoAccount = useIsMessagingAnalyticsDemoAccount();
  const { noLocationsHaveAclAccess } = useHasAclAccess({
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });
  const { isLoadingLocations, preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'SMS',
  });

  const { data = {}, isLoading } = useFetchAggregatedSMSReport('MessageCategory', {
    end_date: appendTime(filters?.endDate),
    location_id: preselectedAccessibleLocationIds || [],
    start_date: appendTime(filters?.startDate),
    time_zone: filters?.timeZone,
  });

  const messageCategories = useMemo(() => {
    const summedCategories: SummedCategories = {};

    Object.values(data).forEach((locationData) => {
      Object.values(locationData).forEach((dateData) => {
        Object.entries(dateData).forEach(([category, { daily }]) => {
          if (!summedCategories[category]) {
            summedCategories[category] = 0;
          }
          summedCategories[category] += daily;
        });
      });
    });

    return summedCategories;
  }, [data]);

  const topCategories = useMemo(() => {
    // Retrieve the top 5 categories
    const categories = Object.entries(messageCategories)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const { counts, total } = categories.reduce(
      (acc, [key, value]) => {
        return {
          counts: { ...acc.counts, [key]: value },
          total: acc.total + value,
        };
      },
      {
        counts: {},
        total: 0,
      }
    );

    const colors: Record<string, string> = {};
    const chartData = {
      groups: categories.map(([name, value], index) => {
        colors[name] = categoryColors[index];

        return {
          name,
          values: {
            [name]: value,
          },
        };
      }),
    };

    return {
      colors,
      counts,
      chartData,
      total,
    };
  }, [messageCategories]);

  useEffect(() => {
    onLoadingStateChange?.(isLoading);
  }, [isLoading]);

  useEffect(() => {
    onLoadData?.(topCategories.total);
  }, [topCategories.total]);

  if (!messagingAnalyticsEnabled) {
    return null;
  }

  return (
    <Chart
      bottomContentRenderer={
        <OverviewInsights title={t('Total Outbound Messages')} value={formatters.shortenNumber(topCategories.total)} />
      }
      colors={topCategories.colors}
      css={dashboardChartStyle}
      isLoading={isLoading || isLoadingLocations}
      labels={{
        ...messagingCategoriesLabels,
        messages: t('Messages'),
      }}
    >
      <Chart.Header
        actions={[
          {
            label: t('Show More'),
            omit: noLocationsHaveAclAccess,
            onClick: () => {
              navigate({
                to: '/analytics/messaging',
              });
            },
            trackingId: 'show-more-messaging-analytics',
          },
        ]}
        leftElement={isDemoAccount && <DemoChip />}
        title={t('Top Message Categories')}
      />
      <Chart.BarChart
        appearance={{
          customXAxisTick: ({ labels, groupName, ...rest }) => {
            return (
              <XAxisLabelValueTick
                {...rest}
                color={topCategories.colors[groupName]}
                label={labels?.[groupName] || groupName}
                labelColor={theme.colors.neutral60}
                value={formatters.value(topCategories.counts[groupName as keyof typeof topCategories.counts])}
              />
            );
          },
          groupsGap: 100,
          height: DASHBOARD_CHART_SIZE,
          margin: { bottom: 20 },
          showXAxis: true,
        }}
        data={topCategories.chartData}
        formatValue={formatters.value}
      />
    </Chart>
  );
});

MessagingCategories.displayName = 'MessagingCategories';
